import { Box, Button, Stack } from "@mui/material"
import { useParams } from "react-router-dom"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { Bound } from "lib/@components/binding/Bound"
import { If } from "lib/@components/if"
import { Else } from "lib/@components/else"
import { useRefresh } from "lib/@hooks/useRefresh"
import { useDialog } from "lib/@hooks/useDialog"
import { confirm } from "lib/@dialogs/confirm"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { NoResults } from "lib/@components/no-results/no-results"
import { TreeToolbar } from "slot-definitions"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { ResolutionPreviewDialog } from "routes/schedule/plugins/legislation-resolutions/resolution-preview-dialog"
import { getCopResolution } from "./controller/get-cop-resolution.controller"
import { saveCopResolutionDecisions } from "./controller/save-cop-resolution-decisions.controller"
import { rebuildCopResolution } from "./controller/rebuild-cop-resolution.controller"
import { saveCopChangeCriticality } from "./controller/save-cop-change-criticality.controller"
import { CopToolbarAcceptAll, CopDecisionsChanged } from "./cop-toolbar-accept-all"
import { CopResolutionContent } from "./cop-resolution-content"

export function CopResolutionDetailPage() {
    const { id } = useParams()
    const resolution = getCopResolution.useResults({ id }, withInitialValue(IS_LOADING))
    const refresh = useRefresh()
    const openPreview = useDialog(<ResolutionPreviewDialog />, { maxWidth: "lg", fullWidth: true })
    CopDecisionsChanged.useEvent(handleBulkDecision, [resolution])

    if (resolution === IS_LOADING) return <LoadingScreen />
    if (!resolution && id) return null

    const changes = resolution?.proposedChanges ?? []

    const parentCrumbs = resolution?.codeOfPracticeId
        ? [
              { label: "Codes of Practice", to: "/app/sfg20admin" },
              {
                  label: resolution.codeOfPracticeName ?? "Group",
                  to: `/app/cop-group/${encodeURIComponent(resolution.codeOfPracticeId)}`,
              },
          ]
        : [{ label: "Codes of Practice", to: "/app/sfg20admin" }]

    return (
        <PageBreadcrumbContainer
            title={resolution ? `${resolution.code} — CoP Resolutions` : "CoP Resolutions"}
            parentCrumbs={parentCrumbs}
        >
            <TreeToolbar.Plug>
                <Stack direction="row" spacing={1} alignItems="center">
                    <CopToolbarAcceptAll id={id} />
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handlePreview}
                        disabled={!resolution || changes.length === 0}
                    >
                        Preview Schedule
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleRebuild}
                        disabled={resolution?.status === "processing"}
                    >
                        Recalculate
                    </Button>
                </Stack>
            </TreeToolbar.Plug>
            <Box flex={1} sx={{ overflowY: "auto", py: 2, px: 1 }}>
                <Bound
                    target={resolution}
                    onDecisionChange={handleDecisionChange}
                    onTextChange={handleTextChange}
                    onProposedTaskChange={handleProposedTaskChange}
                    onCriticalityChange={handleCriticalityChange}
                >
                    <If truthy={resolution}>
                        <CopResolutionContent />
                        <Else>
                            <NoResults message="Resolution record not found." />
                        </Else>
                    </If>
                </Bound>
            </Box>
        </PageBreadcrumbContainer>
    )

    function handleBulkDecision(decision) {
        const changes = resolution?.proposedChanges ?? []
        for (const c of changes) {
            c.decision = decision
        }
        refresh()
    }

    async function handleDecisionChange(changeId, decision) {
        await saveCopResolutionDecisions({ id, decisions: [{ changeId, decision }] })
        refresh()
    }

    async function handleTextChange(changeId, editedText) {
        await saveCopResolutionDecisions({ id, decisions: [{ changeId, editedText }] })
        refresh()
    }

    async function handleProposedTaskChange(changeId, proposedTask) {
        await saveCopResolutionDecisions({ id, decisions: [{ changeId, proposedTask }] })
        refresh()
    }

    async function handleCriticalityChange(changeId, criticality) {
        await saveCopChangeCriticality({ id, changeId, criticality })
        refresh()
    }

    async function handlePreview() {
        await openPreview({ resolution })
    }

    async function handleRebuild() {
        const ok = await confirm(
            `Recalculate resolutions for "${resolution.code}"? This will discard existing proposals and decisions.`,
            "Recalculate"
        )
        if (!ok) return
        try {
            await rebuildCopResolution({ id })
            successSnackbar("Recalculation queued — results will appear shortly")
        } catch (e) {
            errorSnackbar(`Failed to queue recalculation: ${e.message}`)
        }
    }
}
